/**
 * 숫자 칸(2026-10-20) — 지우면 빈 칸으로 남고, 비운 채 저장하면 그 칸을 짚어 준다.
 *
 * 예전 칸들은 바뀔 때마다 `Number(값)` 으로 담아, 백스페이스로 다 지우면 곧바로 0(바로 보내기는 15)이 다시 섰다 —
 * 지우고 새로 칠 수가 없어 "05" 처럼 앞에 붙었다. 이 칸은 비면 `null` 을 알린다(부르는 쪽은 `null` 을 그대로 들고 있는다).
 *
 * 비었거나 범위를 벗어난 값은 **저장할 때** 막는다. 부르는 쪽이 `numberProblem` 으로 가려 `invalid` 를 켜고(칸이
 * 빨개진다) 안내를 띄운 뒤 `focusFirstInvalid` 로 그 칸에 데려간다. 치는 동안에는 막지 않는다 — 지우고 다시 치는
 * 사이마다 빨간 칸이 깜빡이면 고치는 중인 사람을 다그치는 셈이다.
 */
import type { InputHTMLAttributes, ReactNode } from 'react';
import { josa } from '../../josa.js';

/** 칸이 받는 값 — 서버 검증과 같은 범위를 적는다. */
export interface NumberRule {
  /** 안내에 쓰는 이름 — 「노출 시간」을 입력하세요. */
  label: string;
  min: number;
  max?: number;
  /** 범위 뒤에 붙는 단위 — 「1~3600초」. */
  unit?: string;
  /** 정수만 받는가(기본). */
  integer?: boolean;
}

function rangeText(rule: NumberRule): string {
  const unit = rule.unit ?? '';
  return rule.max === undefined ? `${rule.min}${unit} 이상` : `${rule.min}~${rule.max}${unit}`;
}

/** 저장할 수 없는 값이면 그 까닭(사람이 읽는 말), 괜찮으면 null. */
export function numberProblem(value: number | null, rule: NumberRule): string | null {
  const range = rangeText(rule);
  if (value === null || !Number.isFinite(value)) return `${rule.label}${josa(rule.label, '을/를')} 입력하세요 (${range})`;
  if ((rule.integer ?? true) && !Number.isInteger(value)) return `${rule.label}${josa(rule.label, '은/는')} 정수로 입력하세요 (${range})`;
  if (value < rule.min || (rule.max !== undefined && value > rule.max)) {
    return `${rule.label}${josa(rule.label, '은/는')} ${range}${rule.max === undefined ? '으로' : ' 사이로'} 입력하세요`;
  }
  return null;
}

/** 칸의 글자를 값으로 — 비었으면 null. `type="number"` 는 숫자로 못 읽는 글자를 빈 문자열로 준다. */
export function parseNumberText(text: string): number | null {
  if (text.trim() === '') return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

/**
 * 잘못된 칸(`aria-invalid="true"`) 가운데 첫 칸으로 데려가 초점을 준다 — [저장] 을 눌렀는데 막혔을 때.
 *
 * 막힌 직후의 렌더가 칸에 표시를 단 뒤에 찾아야 하므로 다음 프레임에 한다. 긴 편성에서는 그 칸이 화면 밖이라,
 * 데려가지 않으면 누른 사람은 안내만 보고 어느 칸인지 찾아 헤맨다.
 */
export function focusFirstInvalid(root: ParentNode | null | undefined): void {
  window.requestAnimationFrame(() => {
    const target = (root ?? document).querySelector<HTMLElement>('[aria-invalid="true"]');
    if (!target) return;
    target.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    target.focus({ preventScroll: true });
  });
}

type NativeProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange' | 'min' | 'max'>;

export function NumberInput({
  value,
  onChange,
  rule,
  invalid = false,
  className = 'input input-narrow',
  ...rest
}: NativeProps & {
  value: number | null;
  onChange: (value: number | null) => void;
  /** 범위 — 칸의 `min`·`max` 와 빨간 칸의 안내(`title`)에 쓴다. */
  rule: NumberRule;
  /** 저장이 막혀 이 칸을 짚는다 — 빨간 테두리와 `aria-invalid`. */
  invalid?: boolean;
}): ReactNode {
  return (
    <input
      {...rest}
      className={className}
      type="number"
      inputMode={(rule.integer ?? true) ? 'numeric' : 'decimal'}
      min={rule.min}
      max={rule.max}
      step={(rule.integer ?? true) ? 1 : 'any'}
      value={value ?? ''}
      aria-invalid={invalid || undefined}
      title={invalid ? (numberProblem(value, rule) ?? undefined) : rest.title}
      onChange={(event) => onChange(parseNumberText(event.target.value))}
    />
  );
}
